import { Suspense, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { ContentLoader, Page } from 'ui'
import { isOpenedSelector } from 'models/window/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { Header } from '../header'
import { Tabs } from '../tabs'
import { BanScreen } from '../main/ban-screen'
import { Onboarding } from '../onboarding'
import { useAuthContext } from '../../core/providers/AuthProvider'
import { openWindow } from '../../models/window/actions'
import { modalNames } from '../modal/constants/names'
import { marketingService, socketClient } from '../../core/services'
import { routes } from '../../core/routes/paths'
import { useBackButton } from '../../core/hooks/useBackButton'

export const Layout = () => {
    useBackButton()
    const dispatch = useDispatch()
    const location = useLocation()
    const { isAuthenticated, user } = useAuthContext()
    const [activePromo, setActivePromo] = useState(null)
    const [referralWelcome, setReferralWelcome] = useState(null)
    const [showOnboarding, setShowOnboarding] = useState(false)

    const isOpened = useSelector(isOpenedSelector)

    useEffect(() => {
        if (isAuthenticated && location.pathname === routes.main && !localStorage.getItem('onboardingCompleted')) {
            setShowOnboarding(true)
        }
    }, [isAuthenticated, location.pathname])

    useEffect(() => {
        if (isOpened) {
            document.body.classList.add('blurred')
        } else {
            document.body.classList.remove('blurred')
        }

        return () => {
            document.body.classList.remove('blurred')
        }
    }, [isOpened])

    useEffect(() => {
        const checkPromo = async () => {
            if (!isAuthenticated) return
            if (showOnboarding) return

            try {
                const response = await marketingService.getActivePromo()
                if (response.success && response.promo) {
                    setActivePromo(response.promo)
                }
            } catch (error) {
                console.error('Failed to load promo:', error)
            }
        }

        checkPromo()
    }, [isAuthenticated, showOnboarding])

    useEffect(() => {
        if (activePromo && !showOnboarding) {
            setTimeout(() => {
                dispatch(
                    openWindow(modalNames.PROMO_CODE_MODAL, {
                        promo: activePromo,
                    })
                )
                if (activePromo.notificationId) {
                    marketingService
                        .markViewed(activePromo.notificationId)
                        .catch(() => {})
                }
                setActivePromo(null)
            }, 400)
        }
    }, [dispatch, activePromo, showOnboarding])

    useEffect(() => {
        const checkReferralWelcome = async () => {
            if (!isAuthenticated || !user || !user.siteId) return
            if (showOnboarding) return

            const hasShownWelcome = localStorage.getItem(
                'hasShownReferralWelcome'
            )
            if (hasShownWelcome) return

            try {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + '/friends/referral-welcome',
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                        },
                    }
                )
                const data = await response.json()

                if (data.success && data.shouldShow) {
                    setReferralWelcome({
                        referrerUsername: data.referrer?.username,
                    })
                }
            } catch (error) {
                console.error('Failed to check referral welcome:', error)
            }
        }

        checkReferralWelcome()
    }, [isAuthenticated, user, showOnboarding])

    useEffect(() => {
        if (referralWelcome && !showOnboarding) {
            setTimeout(() => {
                dispatch(
                    openWindow(modalNames.REFERRAL_WELCOME_MODAL, {
                        referrerUsername: referralWelcome.referrerUsername,
                    })
                )
                localStorage.setItem('hasShownReferralWelcome', 'true')
                setReferralWelcome(null)
            }, 800)
        }
    }, [dispatch, referralWelcome, showOnboarding])

    useEffect(() => {
        if (!isAuthenticated) return

        const handleNewPromo = async (data) => {
            try {
                const response = await marketingService.getActivePromo()
                if (response.success && response.promo) {
                    setActivePromo(response.promo)
                }
            } catch (error) {
                console.error('Failed to load new promo:', error)
            }
        }

        socketClient.on('new_promo', handleNewPromo)

        return () => {
            socketClient.off('new_promo', handleNewPromo)
        }
    }, [isAuthenticated])

    // Если пользователь забанен - показываем ТОЛЬКО бан-экран без навигации
    if (isAuthenticated && user?.isBanned) {
        return (
            <Page isAuth={false}>
                <BanScreen reason={user.banReason} />
            </Page>
        )
    }

    return (
        <>
            <Page isAuth={isAuthenticated}>
                {isAuthenticated && user && <Header />}
                <Suspense fallback={<ContentLoader />}>
                    <Outlet />
                </Suspense>
                {isAuthenticated && user && <Tabs />}
                {showOnboarding && (
                    <Onboarding onComplete={() => setShowOnboarding(false)} />
                )}
            </Page>
        </>
    )
}
